import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRunDetail, RunDetail, RunDetailOptions } from '../../../api/runs';

const MAX_SHARED_PAGES = 64;
const sharedPages = new Map<string, RunDetail>();
const sharedRequests = new Map<string, Promise<RunDetail>>();

const optionsKey = (options?: RunDetailOptions) => JSON.stringify(
  Object.entries(options ?? {}).sort(([left], [right]) => left.localeCompare(right))
);

const requestKey = (
  runId: string,
  projectId: string,
  options?: RunDetailOptions
) => `${projectId}:${runId}:${optionsKey(options)}`;

const remember = (key: string, data: RunDetail) => {
  sharedPages.delete(key);
  sharedPages.set(key, data);
  while (sharedPages.size > MAX_SHARED_PAGES) {
    const oldest = sharedPages.keys().next().value;
    if (typeof oldest !== 'string') break;
    sharedPages.delete(oldest);
  }
};

const validateResponse = (
  data: RunDetail,
  runId: string,
  projectId: string,
  options?: RunDetailOptions
) => {
  if (data.run_id !== runId || data.project_id !== projectId) {
    throw new Error('Saved response does not match this run and project.');
  }
  if (options?.view && data.detail_view !== options.view) {
    throw new Error('The server did not return the requested saved detail view.');
  }
  return data;
};

const sharedRead = (
  runId: string,
  projectId: string,
  options?: RunDetailOptions
) => {
  const key = requestKey(runId, projectId, options);
  const cached = sharedPages.get(key);
  if (cached) {
    // LRU refresh. A completed saved response is immutable.
    remember(key, cached);
    return Promise.resolve(cached);
  }
  const pending = sharedRequests.get(key);
  if (pending) return pending;

  // Do not bind this request to a single component mount. React StrictMode and
  // collapsing an evidence row may unmount one subscriber while another mount
  // still needs the same immutable response. Subscribers independently reject
  // stale results through their generation checks below.
  const requestController = new AbortController();
  const request = fetchRunDetail(runId, options, requestController.signal)
    .then(data => validateResponse(data, runId, projectId, options))
    .then(data => {
      remember(key, data);
      return data;
    })
    .finally(() => sharedRequests.delete(key));
  sharedRequests.set(key, request);
  return request;
};

/** Test isolation for the module-level immutable saved-page cache. */
export const resetSavedRunViewCacheForTests = () => {
  sharedPages.clear();
  sharedRequests.clear();
};

/** Lazy saved reads share immutable pages and never invoke selection/evaluation. */
export function useSavedRunView(runId: string, projectId: string) {
  const scope = useRef({ runId, projectId, generation: 0, mounted: true });
  if (scope.current.runId !== runId || scope.current.projectId !== projectId) {
    scope.current = {
      runId,
      projectId,
      generation: scope.current.generation + 1,
      mounted: true,
    };
  }
  const [state, setState] = useState<{
    runId: string;
    projectId: string;
    data?: RunDetail;
    loading: boolean;
    error?: string;
  }>({ runId, projectId, loading: false });

  useEffect(() => {
    scope.current.mounted = true;
    return () => {
      scope.current.mounted = false;
      scope.current.generation += 1;
    };
  }, []);

  const load = useCallback(async (options?: RunDetailOptions) => {
    const generation = ++scope.current.generation;
    setState(previous => ({
      ...(previous.runId === runId && previous.projectId === projectId ? previous : {}),
      runId,
      projectId,
      loading: true,
      error: undefined,
    }));
    try {
      const data = await sharedRead(runId, projectId, options);
      if (!scope.current.mounted || generation !== scope.current.generation) return;
      setState({ runId, projectId, data, loading: false });
    } catch (error) {
      if (!scope.current.mounted || generation !== scope.current.generation) return;
      const message = error instanceof Error && error.message.includes('requested saved detail view')
        ? `${error.message} Reload this saved run to use its supported response.`
        : 'Saved details could not be loaded. Retry the read.';
      setState({ runId, projectId, loading: false, error: message });
    }
  }, [runId, projectId]);

  const current = state.runId === runId && state.projectId === projectId;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : undefined,
    loading: current && state.loading,
    load,
  };
}
